import moment from 'moment';

import get from 'lodash/get';
import map from 'lodash/map';
import size from 'lodash/size';
import last from 'lodash/last';
import first from 'lodash/first';
import sumBy from 'lodash/sumBy';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import round from 'lodash/round';
import floor from 'lodash/floor';
import slice from 'lodash/slice';
import filter from 'lodash/filter';
import meanBy from 'lodash/meanBy';
import reverse from 'lodash/reverse';
import forEach from 'lodash/forEach';
import groupBy from 'lodash/groupBy';
import toPairs from 'lodash/toPairs';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import takeRight from 'lodash/takeRight';
import takeWhile from 'lodash/takeWhile';
import findIndex from 'lodash/findIndex';

import {
    BASE_RATING,
    DAYS_IN_WEEK,
    HOURS_IN_DAY,
    FINISHES_NEGATIVE,
    FINISHES_POSITIVE,
    FINISHES_EVEN,
    LEVEL_25_POINTS_TOTAL,
    SEASON_TRACK_EARLY_POINTS,
    SEASON_TRACK_LATE_POINTS
} from '../const';

import { ServerGameData, ServerPlayerSeasonData } from '../../data_types/interfaces';

import {
    StreakMeta,
    SummaryData,
    NormalizedGameData,
    GroupedRounds,
    RatingChangeGroup,
    AdvancedGroupedRound
} from '../../data_types/types';


const squishPreviousSeasonRating = (previousSeasonRating: number)=>
    Math.round((previousSeasonRating - BASE_RATING) * 0.2 + BASE_RATING)


/**
 * Normalize the JSON data to use integers and dates. It also appends the
 * running current rating to each round to make it easier to track.
 */
export const normalizeHistoryData = (
    seasonIndex: number,
    previousSeasonRating: number | null,
    historyData: ServerGameData[]
): NormalizedGameData[] => {

    var currentRating = BASE_RATING;

    if (seasonIndex && seasonIndex > 2 && previousSeasonRating) {
        currentRating = squishPreviousSeasonRating(previousSeasonRating);
        // console.log('use previous season compressed rating for starting point: ' + currentRating);
     }

    var previousRatingChange: number | null = null;

    const sortedHistoryData: ServerGameData[] = Object.entries(historyData)
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .map(([date, gameData]) => ({ ...gameData, date: date }));

    const filteredHistoryData: ServerGameData[] = filter(sortedHistoryData, (data)=> data.position !== 'pending');

    return map(filteredHistoryData, (matchData, idx: number)=> {
        const ratingChange = matchData.ratingChange;

        const shouldMarkAsBounce = (
          previousRatingChange !== null &&
          previousRatingChange < 0 &&
          ratingChange < 0
        );

        currentRating = matchData.rating;
        previousRatingChange = ratingChange;

        return {
            ...matchData,
            date: Number(matchData.date),
            matchNum: idx + 1,
            ratingChange: ratingChange,
            seasonPoints: matchData.points,
            currentRating: currentRating,
            bounce: shouldMarkAsBounce ? 1 : 0
        };
    });
};


/**
 * Calculate all of the tabular summary data for display
 */
export const calculateSummaryData = (historyData: NormalizedGameData[], seasonIndex: number, playerSeasonData: ServerPlayerSeasonData): SummaryData => {
    const roundsByDate = groupRoundsByDate(historyData);
    const roundsByHour = groupRoundsByHour(historyData);
    const roundsByDayOfWeek = groupRoundsByDayOfWeek(historyData);
    const groupedRatingChanges = groupByRatingChange(historyData);

    const totalSeasonPoints = getTotalSeasonPoints(historyData);

    return {
        total_rounds: size(historyData),
        rounds_by_date: roundsByDate,
        rounds_by_hour: roundsByHour,
        rounds_by_day_of_week: roundsByDayOfWeek,
        last_round: getLastRoundPlayed(historyData),
        grouped_rating_changes: groupedRatingChanges,
        most_rounds_for_day: getMostRoundsInDay(roundsByDate),
        rounds_per_day: getRoundsPerDay(historyData),
        current_rating: playerSeasonData.rating,
        highest_rating: getHighestRating(historyData, playerSeasonData.rating),
        mean_points_gained_per_round: getMeanPointsPerRound(historyData),
        last_twenty_rating: getLastTwentyRating(historyData),
        bounce_back_percentage: getBounceBackPercentage(historyData, groupedRatingChanges),
        longest_green_streak: getLongestGreenStreak(historyData),
        longest_red_streak: getLongestRedStreak(historyData),
        longest_non_green_streak: getLongestNonGreenStreak(historyData),
        longest_non_red_streak: getLongestNonRedStreak(historyData),
        longest_points_gained_streak: getLongestPointsGainedStreak(historyData),
        longest_points_lost_streak: getLongestPointsLostStreak(historyData),
        current_streak: getCurrentStreak(historyData),
        rounds_by_hour_advanced: getAdvancedGroupedRoundMetrics(historyData, roundsByHour),
        rounds_by_day_of_week_advanced: getAdvancedGroupedRoundMetrics(historyData, roundsByDayOfWeek),
        total_season_points: totalSeasonPoints,
        season_points_per_round: getSeasonPointsPerRound(historyData),
        rounds_per_post_track_reward: getRoundsPerPostTrackReward(historyData),
        current_mp_level: getCurrentMpLevel(totalSeasonPoints, seasonIndex)
    };
};


/**
 * Bounce back percentage shows how well the player recovers after consecutive
 * losses. A higher percentage shows their ability to avoice a long slump.
 */
const getBounceBackPercentage = (historyData: NormalizedGameData[], groupedRatingChanges: RatingChangeGroup)=>
    round((1 - (sumBy(historyData, 'bounce') / get(groupedRatingChanges[FINISHES_NEGATIVE], 'total'))) * 100) || 0;


/**
 * Last twenty rating describes the points gained over the last twenty matches.
 */
const getLastTwentyRating = (historyData: NormalizedGameData[])=>
    sumBy(takeRight(historyData, 20), 'ratingChange');


/**
 * Last twenty rating describes the points gained over the last twenty matches.
 */
const getLastRoundPlayed = (historyData: NormalizedGameData[])=>
    get(last(historyData), 'date') || 0;


/**
 * Mean points per round shows how many points the player typically gains per round.
 */
const getMeanPointsPerRound = (historyData: NormalizedGameData[])=>
    round(meanBy(historyData, 'ratingChange'), 2) || 0;


/**
 * The highest rating describes the best rating the player has achieved thus far.
 */
const getHighestRating = (historyData: NormalizedGameData[], currentRating: number)=> {
    const highestHistoryRating = get(maxBy(historyData, 'currentRating'), 'currentRating', BASE_RATING);

    if (currentRating > highestHistoryRating) {
        return currentRating;
    }

    return highestHistoryRating;
};


/**
 * Rounds per day describes the average number of rounds the player plays in a day.
 */
const getRoundsPerDay = (historyData: NormalizedGameData[])=> {
    var totalDays = moment().diff(moment(get(first(historyData), 'date'), true), 'days');
    const numMatches = size(historyData);

    // account for first day
    if (totalDays <= 0) {
        totalDays = 1;
    }

    return round(numMatches / totalDays, 1);
}


/**
 * Most rounds in a day describes the day in which they played the most rounds.
 */
const getMostRoundsInDay = (roundsByDate: GroupedRounds): [string, NormalizedGameData[]] => {
    const pairedRounds = toPairs(roundsByDate);

    const maxRoundsByDay = maxBy(pairedRounds, (pairs: [string, NormalizedGameData[]])=> {
        const roundRecordsForDay = last(pairs);

        if (roundRecordsForDay) {
            return roundRecordsForDay.length;
        }

        return 0;
    });

    if (!maxRoundsByDay) {
        return [moment().format('MM/DD/YYYY'), []];
    }

    return maxRoundsByDay;
};


/**
 * Group rounds by the actual date
 */
const groupRoundsByDate = (historyData: NormalizedGameData[])=>
    groupBy(historyData, (o)=> moment(o.date).format('MM/DD/YYYY')) || {};


/**
 * Group rounds by the hour they played
 */
const groupRoundsByHour = (historyData: NormalizedGameData[])=> ({
    ...HOURS_IN_DAY,
    ...groupBy(historyData, (o)=> moment(o.date).hour())
});


/**
 * Group rounds by the day of the week they played
 */
const groupRoundsByDayOfWeek = (historyData: NormalizedGameData[])=> ({
    ...DAYS_IN_WEEK,
    ...groupBy(historyData, (o)=> moment(o.date).day())
});


/**
 * Calculate advanced metrics about a grouping of rounds. The groupedRounds
 * should be in an object format with each key containing an array of rounds as
 * the value.
 */
const getAdvancedGroupedRoundMetrics = (historyData: NormalizedGameData[], groupedRounds: GroupedRounds): AdvancedGroupedRound[] => {
    return map(groupedRounds, (rounds)=> {
        const totalChange = sumBy(rounds, 'ratingChange');

        return {
            count: rounds.length,
            percent_of_total: (
            rounds.length > 0 ?
              round((rounds.length / size(historyData)) * 100, 1)
            :
              0
            ),
            total_change: totalChange,
            average_change: round(totalChange / rounds.length, 1) || 0,
            gain_percent: round((filter(rounds, (round)=> round.ratingChange > 0).length / rounds.length) * 100, 1) || 0,
            even_percent: round((filter(rounds, (round)=> round.ratingChange === 0).length / rounds.length) * 100, 1) || 0,
            loss_percent: round((filter(rounds, (round)=> round.ratingChange < 0).length / rounds.length) * 100, 1) || 0
        };
    })
};


/**
 * Creates a grouping showing the totals for point gained, lost, and even.
 */
const groupByRatingChange = (historyData: NormalizedGameData[]): RatingChangeGroup => {
    const changeTypes = {
        [FINISHES_POSITIVE]: { total: 0, percent: 0 },
        [FINISHES_NEGATIVE]: { total: 0, percent: 0 },
        [FINISHES_EVEN]: { total: 0, percent: 0 }
    };

    forEach(historyData, (matchData)=> {
        if (matchData.ratingChange === 0) {
            changeTypes[FINISHES_EVEN].total += 1;

        } else if (matchData.ratingChange > 0) {
            changeTypes[FINISHES_POSITIVE].total += 1;

        } else {
            changeTypes[FINISHES_NEGATIVE].total += 1;

        }
    });

    changeTypes[FINISHES_EVEN].percent = round((changeTypes[FINISHES_EVEN].total / size(historyData)) * 100) || 0;
    changeTypes[FINISHES_POSITIVE].percent = round((changeTypes[FINISHES_POSITIVE].total / size(historyData)) * 100) || 0;
    changeTypes[FINISHES_NEGATIVE].percent = round((changeTypes[FINISHES_NEGATIVE].total / size(historyData)) * 100) || 0;

    return changeTypes;
};


/**
 * Utility function to generate a list of streaks based on a
 * streakQualifierFunc. This creates a meta object for each streak summarzing
 * the streak information. This meta is meant to be consumed by streak parsers.
 *
 * Note: We're doing some index hacking here since lodash doesn't have a
 * convenient way to chunk data based on criteria. This works but there is
 * probably a more elegant way to do this.
 */
const generateStreakMeta = (
    historyData: NormalizedGameData[],
    streakQualifierFunc: (m: NormalizedGameData)=> boolean,
    includesZero: boolean
): StreakMeta[]=> {

    const allStreaks = [];

    for (var i = 0; i < size(historyData); i++) {
        const currentSlice = slice(historyData, i);
        const streak = takeWhile(currentSlice, streakQualifierFunc);
        const idx = findIndex(historyData, last(streak)) + 1;

        if (idx > i) {
            i = idx;
            allStreaks.push(streak);
        }
    }

    const allStreaksWithMeta = map(allStreaks, (streak)=> {
        return {
            len: streak.length,
            includesZero: includesZero,
            points_gained: sumBy(streak, 'ratingChange'),
            start_match_num: get(first(streak), 'matchNum') || 0,
            end_match_num: get(last(streak), 'matchNum') || 0,
            start_date: get(first(streak), 'date') || 0,
            end_date: get(last(streak), 'date') || 0
        };
    });

    return allStreaksWithMeta || [{} as StreakMeta];
};


/**
 * Return the longest streak where only positive finishes occurred
 */
const getLongestGreenStreak = (historyData: NormalizedGameData[])=>
    maxBy(generateStreakMeta(historyData, (m: NormalizedGameData)=> m.ratingChange > 0, false), 'len') || {} as StreakMeta;


/**
 * Return the longest streak where only negative finishes occurred
 */
const getLongestRedStreak = (historyData: NormalizedGameData[])=>
    maxBy(generateStreakMeta(historyData, (m: NormalizedGameData)=> m.ratingChange < 0, false), 'len') || {} as StreakMeta;


/**
 * Return the longest streak where negative and even finishes occurred
 */
const getLongestNonGreenStreak = (historyData: NormalizedGameData[])=>
    maxBy(generateStreakMeta(historyData, (m: NormalizedGameData)=> m.ratingChange <= 0, true), 'len') || {} as StreakMeta;


/**
 * Return the longest streak where positive and even finishes occurred
 */
const getLongestNonRedStreak = (historyData: NormalizedGameData[])=>
    maxBy(generateStreakMeta(historyData, (m: NormalizedGameData)=> m.ratingChange >= 0, true), 'len') || {} as StreakMeta;


/**
 * Return the streak where the most points were gained no matter the length
 */
const getLongestPointsGainedStreak = (historyData: NormalizedGameData[])=>
    maxBy(generateStreakMeta(historyData, (m: NormalizedGameData)=> m.ratingChange >= 0, true), 'points_gained') || {} as StreakMeta;


/**
 * Return the streak where the most points were lost no matter the length
 */
const getLongestPointsLostStreak = (historyData: NormalizedGameData[])=>
    minBy(generateStreakMeta(historyData, (m: NormalizedGameData)=> m.ratingChange <= 0, true), 'points_gained') || {} as StreakMeta;

/**
 * Return the current streak the player is on. We use a hierarchy to define
 * which kind of streak to display to the user:
 *
 * - Only green (if > 2 matches)
 * - Only red (if > 2 matches)
 * - No red
 * - No green
 *
 * This allows us to be more optimistic about the streak they are currently on.
 */
const getCurrentStreak = (historyData: NormalizedGameData[])=> {
    // Copy first: lodash reverse mutates, which flipped the shared history array.
    const reversedHistory = reverse([...historyData]);

    const noRedStreak = takeWhile(reversedHistory, (match)=> match.ratingChange >= 0);
    const onlyGreenStreak = takeWhile(reversedHistory, (match)=> match.ratingChange > 0);
    const noGreenStreak = takeWhile(reversedHistory, (match)=> match.ratingChange <= 0);
    const onlyRedStreak = takeWhile(reversedHistory, (match)=> match.ratingChange < 0);

    var bestStreak: NormalizedGameData[] = [];

    if (!isEmpty(noGreenStreak)) {
        bestStreak = noGreenStreak;
    }

    if (!isEmpty(noRedStreak)) {
        bestStreak = noRedStreak;
    }

    if (!isEmpty(onlyRedStreak) && onlyRedStreak.length > 2) {
        bestStreak = onlyRedStreak;
    }
    
    if (!isEmpty(onlyGreenStreak) && onlyGreenStreak.length > 2) {
        bestStreak = onlyGreenStreak;
    }

    return {
        len: bestStreak.length,
        includesZero: includes(map(bestStreak, 'ratingChange'), 0),
        points_gained: sumBy(bestStreak, 'ratingChange'),
        start_match_num: get(last(bestStreak), 'matchNum', 0),
        end_match_num: get(first(bestStreak), 'matchNum', 0),
        start_date: get(last(bestStreak), 'date', 0),
        end_date: get(first(bestStreak), 'date', 0)
    };
};

/**
 * Return the total season points accumulated this season
 */
const getTotalSeasonPoints = (historyData: NormalizedGameData[])=>
    sumBy(historyData, 'seasonPoints');

/**
 * Return the average amount of season points accumulated in each round played
 */
const getSeasonPointsPerRound = (historyData: NormalizedGameData[])=>
    round(getTotalSeasonPoints(historyData) / size(historyData), 1) || 0;

/**
 * Return the number of rounds it takes to reach a 500pt reward
 */
const getRoundsPerPostTrackReward = (historyData: NormalizedGameData[])=>
    round(500 / getSeasonPointsPerRound(historyData), 1) || 0;

/**
 * Get the current player's MP level
 */
const getCurrentMpLevel = (totalSeasonPoints: number, seasonIndex: number)=> {
    if (totalSeasonPoints <= LEVEL_25_POINTS_TOTAL) {
        return totalSeasonPoints / SEASON_TRACK_EARLY_POINTS;
    }

    // Seasons 1-4 all had the model of 500pts for late game rewards
    if (seasonIndex && seasonIndex <= 3) {
        return floor(((totalSeasonPoints - LEVEL_25_POINTS_TOTAL ) / SEASON_TRACK_LATE_POINTS) + 25, 0);
    }

    // @TODO add new logic for determining season points past season 4

    return floor(((totalSeasonPoints - LEVEL_25_POINTS_TOTAL ) / SEASON_TRACK_LATE_POINTS) + 25, 0);
};
